import {
    createTheme,
    rem,
    defaultVariantColorsResolver,
    Button,
    Tooltip,
    Modal,
    darken,
    Menu,
    MantineColorsTuple,
} from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/spotlight/styles.css";
import "@mantine/dates/styles.css";
import classes from "./variants.module.css";
import "./global.css";
import "./theme.css";

// Mantine spacing
// 10 12 16 20 32
const dibblue: MantineColorsTuple = [
    "#dce4f5",
    "#b9c7e2",
    "#94a8d0",
    "#748dc0",
    "#5f7cb7",
    "#5474b4",
    "#44639f",
    "#3a5890",
    "#2c4b80",
    "#012C3D",
];

const gneis: MantineColorsTuple = [
    "#F4F5F5",
    "#E8E8E8",
    "#CFCFCF",
    "#B4B4B4",
    "#9D9D9D",
    "#8F8F8F",
    "#888888",
    "#757575",
    "#666868",
    "#535B5B",
];

const sjøgrønn: MantineColorsTuple = [
    "#F0F9F7",
    "#E3F0ED",
    "#C1E2DA",
    "#9BD2C5",
    "#7DC5B3",
    "#6ABDA8",
    "#5EBAA3",
    "#4EA38E",
    "#41917D",
    "#2F7E6C",
];

const blåbær: MantineColorsTuple = [
    "#ededfd",
    "#d6d5f5",
    "#a8a8ec",
    "#7977e5",
    "#524edf",
    "#3b35dc",
    "#2f28dc",
    "#241dc3",
    "#1d19af",
    "#14149a",
];

const mars: MantineColorsTuple = [
    "#fff2e3",
    "#fce4d1",
    "#f3c8af",
    "#EAAB79",
    "#E39152",
    "#DF8138",
    "#DD792A",
    "#C4671D",
    "#AF5A16",
    "#994C0B",
];

export const DIB_BRAND_BASE = "#012C3D";
export const DIB_BRAND_ACCENT = "#DE7F35";
export const KAI_BRAND_BASE = "#171736";
export const KAI_BRAND_ACCENT = "#f4fbfe";

// ========================================
// TYPOGRAPHY CONFIGURATION
// ========================================
// For designers: Uncomment and modify values below to customize typography
// All commented values are Mantine's defaults

export const cssVariablesResolver = () => ({
    variables: {},
    light: {"--mantine-color-body": "#fbfbfb"},
    dark: {},
});

export default createTheme({
    colors: {
        dibblue,
        gneis,
        sjøgrønn,
        blåbær,
        mars,
    },
    cursorType: "pointer",

    // ========================================
    // TYPOGRAPHY SETTINGS
    // ========================================
    fontFamily: "Lato, sans-serif",

    // Custom font sizes (extend Mantine's xs, sm, md, lg, xl)
    fontSizes: {
        xxl: rem(24), // 24px - Custom extra-extra large
        xxxl: rem(30), // 30px - Custom extra-extra-extra large
        // Uncomment to override default sizes:
        // xs: rem(12),
        // sm: rem(14),
        // md: rem(16),
        // lg: rem(18),
        // xl: rem(20),
    },

    // Line heights for text sizes
    lineHeights: {
        // Uncomment to override default line heights:
        // xs: 1.4,
        // sm: 1.45,
        // md: 1.55,
        // lg: 1.6,
        // xl: 1.65,
    },

    // Heading configuration (h1-h6)
    headings: {
        // Uncomment to set a custom font family for all headings:
        // fontFamily: "Lato, sans-serif",

        // Uncomment to set font weight for all headings (default: 700):
        // fontWeight: '700',

        // Individual heading sizes
        sizes: {
            // Uncomment to customize each heading level:
            // h1: {fontSize: rem(34), lineHeight: '1.3'},
            // h2: {fontSize: rem(26), lineHeight: '1.35'},
            // h3: {fontSize: rem(22), lineHeight: '1.4'},
            // h4: {fontSize: rem(18), lineHeight: '1.45'},
            // h5: {fontSize: rem(16), lineHeight: '1.5'},
            // h6: {fontSize: rem(14), lineHeight: '1.5'},
        },
    },

    spacing: {
        "2xs": rem(8),
        xs: rem(10),
        sm: rem(12),
        md: rem(16),
        lg: rem(20),
        xl: rem(32),
    },

    breakpoints: {
        xs: "30em",
        sm: "48em",
        md: "64em",
        lg: "74em",
        xl: "90em",
    },
    components: {
        Button: Button.extend({
            classNames: classes,
            defaultProps: {size: "xs", radius: "md"},
        }),
        Tooltip: Tooltip.extend({
            defaultProps: {
                openDelay: 300,
                position: "bottom",
            },
        }),
        Modal: Modal.extend({
            defaultProps: {
                radius: "lg",
                transitionProps: {transition: "fade", duration: 150},
            },
            styles: {
                title: {
                    fontWeight: 700,
                },
            },
        }),
        Menu: Menu.extend({
            defaultProps: {
                radius: "md",
            },
        }),
    },
    variantColorResolver: (input) => {
        const defaultResolvedColors = defaultVariantColorsResolver(input);

        if (input.variant === "message-button") {
            return {
                background: "transparent",
                hover: "var(--mantine-color-gray-1)",
                color: "var(--mantine-color-gray-text)",
                border: "none",
            };
        }

        if (input.variant === "rounded") {
            return defaultVariantColorsResolver({
                ...input,
                variant: "light",
            });
        }

        return defaultResolvedColors;
    },
});
